import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import isEmpty from 'lodash-es/isEmpty';
import isString from 'lodash-es/isString';

import useEventCallback from '@ecp/common/src/hooks/useEventCallback';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import AllCheckboxGroup from '@ecp/common/src/components/checkbox/AllCheckboxGroup';
import Input from '@ecp/common/src/components/input/bo/Input';
import Popup from '@ecp/common/src/components/modal/bo/Popup';

import { useAlertDialog } from '@bo-hooks/common/useModal';
import userApi from '@bo-apis/user/userApi';
import { userGridOptions } from '@bo-components/common/components/user/searchUserAutocompleteConsts';
import Grid from '@bo-components/common/grid/Grid';
import Search from '@bo-components/common/search/Search';
import SearchItem from '@bo-components/common/search/SearchItem';
import SearchRow from '@bo-components/common/search/SearchRow';

const SearchUserPopupContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SearchKeywordContain = styled(FlexBoxColumn)`
  padding: 6px 0;
`;

const useYnItems = [
  { value: 'Y', labelText: '사용' },
  { value: 'N', labelText: '미사용' },
];

const initFormData = {
  userId: '',
  userName: '',
  jobGroupName: '',
  useYnCodes: ['Y', 'N'],
};

function SearchUserPopup({ open, onClose, onConfirm }) {
  const [form, setForm] = useState({ ...initFormData });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [userTotal, setUserTotal] = useState(0);
  const [userGridList, setUserGridList] = useState([]);
  const lastSearchForm = useRef({ ...initFormData });
  const userGridRef = useRef(null);

  const { showAlertDialog } = useAlertDialog();

  const clearSearchForm = useCallback(() => {
    setForm({ ...initFormData });
  }, []);

  const handleSearchWithPagination = useCallback(async (pageable) => {
    const formData = lastSearchForm.current;
    const trimmedForm = Object.keys(formData).reduce((acc, key) => {
      if (formData[key]) {
        acc[key] = isString(formData[key]) ? formData[key].trim() : formData[key];
      }
      return acc;
    }, {});
    const { result: { content: userList = [], totalElements: total } = {} } = await userApi.getUserBySearchOptions({
      form: {
        ...trimmedForm,
        useYn: trimmedForm.useYnCodes.length === 1 ? trimmedForm.useYnCodes[0] : '',
      },
      pageable,
    });
    setUserGridList(userList || []);
    setUserTotal(total);
  }, []);

  const handleSearch = useCallback(
    (e) => {
      (async () => {
        e.preventDefault();
        e.stopPropagation();

        userGridRef?.current?.setPageIndex(0);
        userGridRef?.current?.clearSort();
        lastSearchForm.current = { ...form };

        await handleSearchWithPagination({ size: userGridRef?.current?.getPageSize(), page: 0, sort: {} });
      })();
    },
    [handleSearchWithPagination, form]
  );

  const handleSearchFormChange = useEventCallback((v, data) => {
    setForm((prev) => ({ ...prev, [data.key]: v }));
  });

  const handleSearchPage = useCallback(
    async (pageIndex, pageSize, sort = {}) => {
      setForm({ ...lastSearchForm.current });
      await handleSearchWithPagination({ size: pageSize, page: pageIndex, sort });
    },
    [handleSearchWithPagination]
  );

  useEffect(() => {
    (async () => {
      await handleSearchWithPagination({ size: userGridRef?.current?.getPageSize(), page: 0, sort: {} });
    })();
  }, [handleSearchWithPagination]);

  const handleClickRow = useCallback(({ row }) => {
    const { userId, userName, Kind } = row;
    if (Kind === 'Header') return;

    setSelectedUserId(userId?.toString() ?? '');
    setSelectedUserName(userName);
  }, []);

  const clearState = useCallback(() => {
    setUserTotal(0);
    setUserGridList([]);
    setForm({ ...initFormData });
    lastSearchForm.current = { ...initFormData };
  }, []);

  const clearUser = useCallback(() => {
    setSelectedUserId('');
    setSelectedUserName('');
  }, []);

  const handleClose = useCallback(() => {
    onClose?.();
    clearState();
    clearUser();
  }, [onClose, clearState, clearUser]);

  const handleConfirm = useCallback(() => {
    if (isEmpty(selectedUserId)) {
      showAlertDialog('등록할 사용자를 선택해주세요.');
      return;
    }
    onConfirm?.({ userId: selectedUserId, userName: selectedUserName });
    clearState();
    clearUser();
  }, [selectedUserId, onConfirm, selectedUserName, clearState, clearUser, showAlertDialog]);

  return (
    <Popup
      type="Large"
      open={open}
      header="BO사용자조회"
      onClose={handleClose}
      onCancelClick={handleClose}
      onConfirmClick={handleConfirm}
    >
      <SearchUserPopupContent>
        <Search onClearClick={clearSearchForm} onSearchClick={handleSearch}>
          <SearchRow>
            <SearchItem label="검색어">
              <SearchKeywordContain>
                <FlexBox align-self="flex-start">
                  <SearchItem label="사용자ID" labelWidth="90px">
                    <Input data-key="userId" onChange={handleSearchFormChange} value={form.userId} width="230px" />
                  </SearchItem>
                  <Spacing right={16} />
                  <SearchItem label="사용자명" labelWidth="90px">
                    <Input data-key="userName" onChange={handleSearchFormChange} value={form.userName} width="230px" />
                  </SearchItem>
                </FlexBox>
                <Spacing bottom={6} />
                <FlexBox align-self="flex-start">
                  <SearchItem label="사용자권한명" labelWidth="90px">
                    <Input
                      data-key="jobGroupName"
                      onChange={handleSearchFormChange}
                      value={form.jobGroupName}
                      width="230px"
                    />
                  </SearchItem>
                </FlexBox>
              </SearchKeywordContain>
            </SearchItem>
          </SearchRow>
          <SearchRow>
            <SearchItem label="사용상태">
              <AllCheckboxGroup
                checked={form.useYnCodes}
                data-key="useYnCodes"
                itemList={useYnItems}
                onChange={handleSearchFormChange}
              />
            </SearchItem>
          </SearchRow>
        </Search>
        <Spacing bottom={30} />
        <Grid
          readOnly
          options={userGridOptions}
          total={userTotal}
          onClick={handleClickRow}
          onSearch={handleSearchPage}
          data={userGridList}
          ref={userGridRef}
        />
      </SearchUserPopupContent>
    </Popup>
  );
}

SearchUserPopup.defaultProps = {};

SearchUserPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default SearchUserPopup;
